import { type SchemaElement, type SchemaTree } from "hyparquet";

/**
 * Build a schema tree from a flat schema array.
 * This is equivalent to hyparquet's internal schemaTree function.
 */
function buildSchemaTree(
  schema: SchemaElement[],
  rootIndex: number = 0,
  path: string[] = [],
): SchemaTree {
  const element = schema[rootIndex];
  const children: SchemaTree[] = [];
  let count = 1;

  if (element.num_children) {
    while (children.length < element.num_children) {
      const childElement = schema[rootIndex + count];
      const child = buildSchemaTree(schema, rootIndex + count, [
        ...path,
        childElement.name,
      ]);
      count += child.count;
      children.push(child);
    }
  }

  return { count, element, children, path };
}

/**
 * Looks up a top-level column by name in a Parquet schema and returns
 * its full subtree, including any nested children. Returns null if the
 * column is not found.
 */
export function findColumnSubtree(
  schema: SchemaElement[],
  columnName: string,
): SchemaTree | null {
  const root = buildSchemaTree(schema, 0, []);
  return root.children.find((c) => c.element.name === columnName) ?? null;
}

/**
 * The function that turns a map key back into the value hyparquet decodes
 * for the key field `element` with parsers that keep temporal values as
 * integers and DECIMAL values as their unscaled integers, or undefined when
 * that value is already a string. hyparquet assembles a map as an object,
 * so it keys each entry by `String(key)`.
 */
export function mapKeyParser(
  element: SchemaElement,
): ((key: string) => unknown) | undefined {
  const { type, converted_type: converted, logical_type: logical } = element;
  if (converted === "DECIMAL" || logical?.type === "DECIMAL") {
    return BigInt;
  }
  if (logical?.type === "FLOAT16") {
    return Number;
  }
  if (logical?.type === "UUID") {
    return undefined;
  }
  switch (type) {
    case "BOOLEAN":
      return (key) => key === "true";
    case "INT32":
    case "FLOAT":
    case "DOUBLE":
      return Number;
    case "INT64":
    case "INT96":
      return BigInt;
    case "FIXED_LEN_BYTE_ARRAY":
      return bytesFromString;
    case "BYTE_ARRAY":
      return converted === "BSON" || logical?.type === "BSON"
        ? bytesFromString
        : undefined;
    default:
      return undefined;
  }
}

/** The bytes whose `String` form (comma-joined) is `key`. */
function bytesFromString(key: string): Uint8Array {
  return key === ""
    ? new Uint8Array()
    : Uint8Array.from(key.split(","), Number);
}
